import { Countdown } from "@/components/DemoBits";
import { LiveDeviceProvider } from "@/components/LiveDevice";
import { ErrorBox, Loading } from "@/components/ui/misc";
import { ApiError, api, q } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { PortalDTO } from "@mm-sales/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  Globe,
  Home,
  Info,
  KeyRound,
  MessageCircle,
  Monitor,
  Presentation,
  ShoppingBag,
  Smartphone,
  Utensils,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { type Brand, brandOf, brandVars } from "./data";
import { AccountAvatar } from "./mocks";
import {
  HardwareSection,
  HomeSection,
  InfoSection,
  MobileSection,
  OnlineShopSection,
  PresentationSection,
  WebsiteSection,
} from "./sections";

/**
 * The prospect's portal: Home / Website / Hardware / Mobile App / Presentation /
 * Customer Info, laid out like the sales mockups and filled with THEIR imported
 * details. Public, scoped by the share token; every section view is reported
 * back as activity so the agent sees what they explored.
 */
const NAV = [
  { to: "", label: "Home", icon: Home, end: true, title: "" },
  { to: "website", label: "Website", icon: Globe, title: "Website Simulator" },
  { to: "shop", label: "Online Shop", icon: ShoppingBag, title: "Online Shop" },
  {
    to: "hardware",
    label: "Hardware",
    icon: Monitor,
    title: "Hardware Simulator",
  },
  {
    to: "mobile",
    label: "Mobile App",
    icon: Smartphone,
    title: "Mobile App Simulator",
  },
  {
    to: "presentation",
    label: "Presentation",
    icon: Presentation,
    title: "Business Review",
  },
  {
    to: "info",
    label: "Customer Info",
    icon: Info,
    title: "Customer Information",
  },
];

/**
 * Loads the prospect's typefaces from Google Fonts, one <link> per family so a
 * family Google doesn't have (a custom webfont) fails alone and the CSS fallback
 * stack takes over for it only.
 */
function BrandFonts({ b }: { b: Brand }) {
  useEffect(() => {
    const added: HTMLLinkElement[] = [];
    for (const fam of b.palette.fontsToLoad) {
      const id = `mm-brand-font-${fam.replace(/\W+/g, "-").toLowerCase()}`;
      if (document.getElementById(id)) continue;
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fam).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;
      document.head.appendChild(link);
      added.push(link);
    }
    return () => {
      for (const l of added) l.remove();
    };
  }, [b.palette.fontsToLoad]);
  return null;
}

const COLLAPSED_KEY = "mm-sales:portal-rail-collapsed";
const LOGINS_KEY = "mm-sales:portal-logins-open";

function CopyValue({
  label,
  value,
  dark,
}: {
  label: string;
  value: string | null | undefined;
  dark: boolean;
}) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      title={`Copy ${label.toLowerCase()}`}
      onClick={() => {
        void navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left",
        dark ? "hover:bg-white/10" : "hover:bg-[#f2f4f8]",
      )}
    >
      <span
        className={cn("w-[78px] shrink-0 text-[11px]", dark ? "text-[#94a3b8]" : "text-[#5b6472]")}
      >
        {label}
      </span>
      <span className="min-w-0 flex-1 truncate font-mono text-[12.5px]" title={value}>
        {value}
      </span>
      {copied ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-[#22c55e]" />
      ) : (
        <Copy className={cn("h-3.5 w-3.5 shrink-0", dark ? "text-[#94a3b8]" : "text-[#5b6472]")} />
      )}
    </button>
  );
}

/**
 * The prospect's demo credentials, one click away from every section: the
 * username/password for the website and the admin app, and the employee PIN the
 * POS asks for. Lives at the bottom of the rail (and behind a chip on phones).
 */
function DemoLogins({
  d,
  token,
  dark,
  collapsed,
  onExpandRail,
}: {
  d: PortalDTO;
  token: string;
  dark: boolean;
  collapsed?: boolean;
  onExpandRail?: () => void;
}) {
  const [open, setOpen] = useState(() => localStorage.getItem(LOGINS_KEY) === "1");
  const toggle = () => {
    if (collapsed) {
      onExpandRail?.();
      localStorage.setItem(LOGINS_KEY, "1");
      setOpen(true);
      return;
    }
    setOpen((o) => {
      localStorage.setItem(LOGINS_KEY, o ? "0" : "1");
      return !o;
    });
  };
  const login = useMutation({
    mutationFn: () => api.post<{ url: string }>(`/v1/portal/${token}/login-link`),
    onSuccess: (r) => window.open(r.url, "_blank"),
  });
  if (d.demo.status !== "active") return null;
  if (!(d.demo.loginEmail || d.demo.loginPassword || d.demo.posPin)) return null;
  const showPanel = open && !collapsed;
  return (
    <div
      className={cn(
        dark ? "border-t border-white/10" : "rounded-xl border border-[#e6e9f0] bg-white",
      )}
    >
      <button
        type="button"
        onClick={toggle}
        title="Your demo logins"
        className={cn(
          "flex w-full items-center gap-3 py-4 text-left",
          dark ? "hover:bg-white/5" : "hover:bg-[#f7f8fb]",
          collapsed ? "justify-center px-2" : "px-5",
        )}
      >
        <KeyRound className={cn("h-6 w-6 shrink-0", dark ? "text-[#94a3b8]" : "text-[#2563eb]")} />
        {!collapsed ? (
          <>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold">Your logins</div>
              <div className={cn("text-[11px]", dark ? "text-[#94a3b8]" : "text-[#5b6472]")}>
                Website, apps &amp; POS
              </div>
            </div>
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                dark ? "text-[#94a3b8]" : "text-[#9aa3b2]",
                showPanel && "rotate-180",
              )}
            />
          </>
        ) : null}
      </button>
      {showPanel ? (
        <div className={cn("space-y-0.5 pb-4", dark ? "px-3" : "px-3")}>
          <CopyValue label="Username" value={d.demo.loginEmail} dark={dark} />
          <CopyValue label="Password" value={d.demo.loginPassword} dark={dark} />
          <CopyValue label="Employee PIN" value={d.demo.posPin} dark={dark} />
          <div
            className={cn(
              "px-2 pt-1.5 text-[10.5px] leading-snug",
              dark ? "text-[#94a3b8]" : "text-[#5b6472]",
            )}
          >
            Username and password sign you in to the website and the admin app. The POS asks for the
            PIN after pairing.
          </div>
          {d.demo.adminUrl ? (
            <button
              type="button"
              onClick={() => login.mutate()}
              disabled={login.isPending}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#2563eb] px-3 py-2 text-[12px] font-semibold text-white hover:bg-[#1d4ed8] disabled:opacity-60"
            >
              {login.isPending ? "Opening…" : "Open your dashboard"}
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function PortalPage() {
  const { token = "" } = useParams();
  // The black rail can be tucked away so the simulators get the full width.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === "1");
  const toggleRail = () =>
    setCollapsed((c) => {
      localStorage.setItem(COLLAPSED_KEY, c ? "0" : "1");
      return !c;
    });
  const portal = useQuery({
    queryKey: ["portal", token],
    queryFn: () => q.portal(token),
    // A blip (API restarting, flaky network) must not read as "invalid link" to a prospect.
    retry: (n, e) => n < 3 && !(e instanceof ApiError && e.status === 404),
    retryDelay: 1500,
  });
  const loc = useLocation();
  const section = NAV.find((n) => n.to && loc.pathname.endsWith(`/${n.to}`)) ?? NAV[0]!;
  const base = `/p/${token}`;
  const b = useMemo(() => (portal.data ? brandOf(portal.data) : null), [portal.data]);

  if (portal.isLoading) return <Loading label="Opening your demo" />;
  if (portal.isError || !portal.data || !b)
    return (
      <div className="mx-auto max-w-md p-10">
        <ErrorBox message="This demo link isn't valid. Ask your MyManager rep for a new one." />
      </div>
    );
  const d = portal.data;
  const expired = d.demo.status !== "active";

  return (
    <LiveDeviceProvider demoId={expired ? null : d.demo.id}>
      <div
        className="flex min-h-screen bg-[#f4f6fb] text-[#111318]"
        style={{ fontFamily: "Inter, system-ui, sans-serif", ...brandVars(b) }}
      >
        <BrandFonts b={b} />
        {/* sidebar */}
        <aside
          className={cn(
            // Pinned to the viewport so the bottom of the rail (logins, help) is always in reach.
            "sticky top-0 hidden h-screen shrink-0 flex-col overflow-y-auto bg-[#0f172a] text-white transition-[width] md:flex",
            collapsed ? "w-[72px]" : "w-[232px]",
          )}
        >
          <button
            type="button"
            onClick={toggleRail}
            title={collapsed ? "Show menu" : "Hide menu"}
            className="absolute -right-3 top-7 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-[#e6e9f0] bg-white text-[#5b6472] shadow hover:text-[#111318]"
          >
            {collapsed ? (
              <ChevronRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronLeft className="h-3.5 w-3.5" />
            )}
          </button>
          <div
            className={cn(
              "flex items-center gap-3 py-6",
              collapsed ? "justify-center px-2" : "px-5",
            )}
          >
            <img src="/brand/mymanager.png" alt="" className="h-9 w-9 shrink-0 object-contain" />
            {!collapsed ? (
              <div>
                <div className="text-[17px] font-extrabold leading-tight">MyManager</div>
                <div className="text-[11px] text-[#94a3b8]">
                  Powering {b.cuisine ? "Restaurants" : "Businesses"}
                </div>
              </div>
            ) : null}
          </div>
          <nav className="flex flex-1 flex-col gap-1 px-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={`${base}/${n.to}`}
                end={n.end}
                title={n.label}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-[14px] font-medium transition-colors",
                    collapsed && "justify-center px-0",
                    isActive
                      ? "bg-[#2563eb] text-white"
                      : "text-[#cbd5e1] hover:bg-white/5 hover:text-white",
                  )
                }
              >
                <n.icon className="h-[18px] w-[18px] shrink-0" /> {!collapsed ? n.label : null}
              </NavLink>
            ))}
          </nav>
          <DemoLogins
            d={d}
            token={token}
            dark
            collapsed={collapsed}
            onExpandRail={() => {
              if (collapsed) toggleRail();
            }}
          />
          <a
            href={`mailto:${d.agent.email}`}
            title="Need help? Chat with our team"
            className={cn(
              "flex items-center gap-3 py-5 hover:bg-white/5",
              collapsed ? "justify-center px-2" : "px-5",
            )}
          >
            <MessageCircle className="h-6 w-6 shrink-0 text-[#94a3b8]" />
            {!collapsed ? (
              <div>
                <div className="text-[13px] font-semibold">Need Help?</div>
                <div className="text-[11px] text-[#94a3b8]">Chat with our team</div>
              </div>
            ) : null}
          </a>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          {/* header */}
          <header className="flex items-center justify-between gap-4 border-b border-[#e6e9f0] bg-white px-6 py-3">
            <div className="flex min-w-0 items-center gap-3">
              {b.logo ? (
                <img
                  src={b.logo}
                  alt=""
                  className="h-10 w-10 rounded-full border border-[#e6e9f0] object-cover"
                />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#fff1f2]">
                  <Utensils className="h-5 w-5" style={{ color: b.color }} />
                </span>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-[16px] font-bold">
                  <span className="truncate">{b.name}</span>
                  {section.title ? (
                    <>
                      <span className="text-[#c1c7d2]">|</span>
                      <span className="truncate font-semibold text-[#5b6472]">{section.title}</span>
                    </>
                  ) : null}
                </div>
                <div className="truncate text-[12px] text-[#5b6472]">
                  {[b.cuisine, b.location].filter(Boolean).join(" · ") || "Your customized demo"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="hidden rounded-md bg-[#eaf0ff] px-2.5 py-1 text-[11px] font-semibold text-[#2563eb] sm:inline">
                Demo Mode
              </span>
              <div className="text-right">
                <div className="text-[18px] font-extrabold leading-tight tabular-nums">
                  <Countdown to={d.demo.expiresAt} compact />
                </div>
                <div className="text-[10px] text-[#5b6472]">Time left in your demo</div>
              </div>
              <div className="flex items-center gap-1">
                <AccountAvatar initials={b.initials} />
                <ChevronDown className="h-4 w-4 text-[#9aa3b2]" />
              </div>
            </div>
          </header>

          {/* mobile nav */}
          <div className="border-b border-[#e6e9f0] bg-white md:hidden">
            <div className="flex gap-1 overflow-x-auto px-3 py-2">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={`${base}/${n.to}`}
                  end={n.end}
                  className={({ isActive }) =>
                    cn(
                      "whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] font-semibold",
                      isActive ? "bg-[#2563eb] text-white" : "text-[#5b6472]",
                    )
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
            {!expired ? (
              <div className="px-3 pb-2">
                <DemoLogins d={d} token={token} dark={false} />
              </div>
            ) : null}
          </div>

          <main className="flex-1 p-5 lg:p-7">
            {expired ? (
              <div className="mx-auto max-w-lg py-16 text-center">
                <h1 className="text-[26px] font-bold">This demo has ended</h1>
                <p className="mt-2 text-[14px] text-[#5b6472]">
                  Your data is saved. {d.agent.name} can extend it or turn it into your live
                  account.
                </p>
                <a
                  href={`mailto:${d.agent.email}`}
                  className="mt-6 inline-flex rounded-lg bg-[#2563eb] px-5 py-2.5 text-[13px] font-semibold text-white"
                >
                  Contact {d.agent.name.split(" ")[0]}
                </a>
              </div>
            ) : (
              <Routes>
                <Route
                  index
                  element={
                    <Track token={token} name="home">
                      <HomeSection b={b} d={d} token={token} base={base} />
                    </Track>
                  }
                />
                <Route
                  path="website"
                  element={
                    <Track token={token} name="website">
                      <WebsiteSection b={b} d={d} />
                    </Track>
                  }
                />
                <Route
                  path="shop"
                  element={
                    <Track token={token} name="shop">
                      <OnlineShopSection b={b} d={d} />
                    </Track>
                  }
                />
                <Route
                  path="hardware"
                  element={
                    <Track token={token} name="hardware">
                      <HardwareSection b={b} d={d} />
                    </Track>
                  }
                />
                <Route
                  path="mobile"
                  element={
                    <Track token={token} name="mobile">
                      <MobileSection b={b} d={d} token={token} />
                    </Track>
                  }
                />
                <Route
                  path="presentation"
                  element={
                    <Track token={token} name="presentation">
                      <PresentationSection b={b} d={d} />
                    </Track>
                  }
                />
                <Route
                  path="info"
                  element={
                    <Track token={token} name="info">
                      <InfoSection b={b} d={d} token={token} />
                    </Track>
                  }
                />
                <Route path="*" element={<Navigate to="" replace />} />
              </Routes>
            )}
          </main>
        </div>
      </div>
    </LiveDeviceProvider>
  );
}

function Track({
  token,
  name,
  children,
}: {
  token: string;
  name: string;
  children: ReactNode;
}) {
  useEffect(() => {
    if (name === "home") return;
    void api
      .post(`/v1/portal/${token}/events`, {
        type: "portal_section_viewed",
        section: name,
      })
      .catch(() => {});
  }, [token, name]);
  return <>{children}</>;
}
